import React, { useState, useRef } from "react";

// services
import { sendToLSQ } from "../services/LeadSquared";
import { Mixpanel, MixpanelEvents } from "../services/Mixpanel";

// seo
import Seo from "../components/Seo";

// constants
import constant from "../constants";

// utils
import { getSessionId } from "../utils";

// custom hooks
import { useCommonPageTracking } from "../hooks/usePageTracking";

// bannerImg
import bannerImg from "../images/forEmployee/banner.png";

// components
import Banner from "../components/Banner";
import LogoCarouselComponent from "../components/Global/LogoCarousel/LogoCarousel";
import EmployeeWellbeingComponent from "../components/ForEmployee/EmployeeWellbeing/EmployeeWellbeing";
import ProvenResultsComponent from "../components/ForEmployee/ProvenResults/ProvenResults";
import EmployeeVideoModalComponent from "../components/ForEmployee/VideoModal/VideoModal";
import FormSectionComponent from "../components/ForEmployee/Form/FormSection";
import ClinicalResearchComponent from "../components/Home/ClinicalResearch/ClinicalResearch";
import Featured from "../components/Home/Featured/Featured";
import FaqComponent from "../components/Home/Faq/Faq";

const ForEmployersPage = props => {
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(getSessionId());
  const formSectionRef = useRef(null);

  // Use common page tracking hooks
  const { deviceType, operatingSystem } = useCommonPageTracking(
    "For Employers Page",
    props.location.search
  );

  const updateSessionId = () => {
    setSessionId(getSessionId());
  };

  // Mixpanel Events -> Cta Click events
  const handleMixpanelCTA = (ctaText, ctaLocation) => {
    Mixpanel.track(MixpanelEvents.contact_button_clicked, {
      page_name: "For Employers Page",
      asset: "India Website",
      asset_name: "India Corporate Website",
      cta_text: ctaText,
      cta_location: ctaLocation,
      device_type: deviceType,
      operating_system: operatingSystem,
    });
  };

  // Mixpanel Events -> FAQ Click Events
  const handleMixpanelCTAFaq = (faqText, faqDesc) => {
    Mixpanel.track(MixpanelEvents.faq_clicked, {
      page_name: "For Employers Page",
      asset: "India Website",
      asset_name: "India Corporate Website",
      faq_text: faqText,
      faq_desc: faqDesc,
      faq_location: "For Employers Page - FAQ Clicked",
      device_type: deviceType,
      operating_system: operatingSystem,
    });
  };

  // Scroll to form section
  const scrollToForm = () => {
    if (formSectionRef.current) {
      formSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Handle form submission
  const handleFormSubmit = async formData => {
    setLoading(true);
    // leadSquared params
    let leadSquaredParams = {
      FirstName: formData.fullName,
      Phone: formData.mobileNumber,
      EmailAddress: formData.emailId,
      Notes: formData.organizationName,
      mx_Website_CTA: "for-employers-page",
      mx_Latest_Source: "New Website",
      mx_Device_Mobile_Desktop: deviceType,
      mx_msc_Operating_System: operatingSystem,
    };

    await sendToLSQ(leadSquaredParams);
    setLoading(false);
  };

  return (
    <>
      <Seo
        title="Healthier Employees, Stronger Business | Twin Health"
        description="Poor employee wellbeing costs more than you think. Give your team a proven metabolic care program to feel better, focus more and boost productivity."
      />
      <Banner
        pageName="For Employers"
        title="Healthier Employees. Stronger Business."
        description="Transform your workforce with an employee care program that improves health, boosts productivity, and reduces workplace stress."
        ctaText="Partner with Twin Health"
        bannerImage={bannerImg}
        imageWidth="614px"
        imageHeight="566px"
        ctaLink=""
        onCtaClick={() => {
          handleMixpanelCTA(
            "Partner with Twin Health",
            "For Employers Page - Banner Section"
          );
          scrollToForm();
        }}
      />

      <LogoCarouselComponent
        title="Trusted by leading organisations across India"
        backgroundColor="linear-gradient(48deg, #fff 0%, #b6cfec 100%)"
      />

      <EmployeeWellbeingComponent />

      <ProvenResultsComponent />

      <EmployeeVideoModalComponent
        ctaText="Play Video"
        onCtaClick={ctaText => {
          handleMixpanelCTA(
            ctaText,
            "For Employee Page - How Twin Support Employee Wellness"
          );
        }}
        sessionId={sessionId}
        updateSessionId={updateSessionId}
        videoName="For Employee Page - How Twin Support Employee Wellness"
        videoLink="https://www.youtube.com/embed/2pg40euLp5w"
        videoPageName="For Employers Page"
      />

      <ClinicalResearchComponent
        carouselData={constant.CLINICAL_RESEARCH_CARDS}
      />

      <Featured />

      <FormSectionComponent
        ref={formSectionRef}
        onFormSubmit={handleFormSubmit}
        mixpanelPageName="For Employers Page"
        loading={loading}
        deviceType={deviceType}
        operatingSystem={operatingSystem}
      />

      <FaqComponent
        title="Frequently Asked Questions"
        accordionPayload={constant.FOR_EMPLOYERS_FAQ}
        onCtaClick={(faqText, faqDesc) =>
          handleMixpanelCTAFaq(faqText, faqDesc)
        }
      />
    </>
  );
};

export default ForEmployersPage;
